import { Box, Chip, Typography } from "@mui/material";
import { getStateLabel } from "components/ui/StateSelect";
import { useReportSettings } from "./ReportSettingsContext";
import { useStateTaxRate } from "hooks/useStateTaxRate";

/**
 * The state tax the strategy report levies — read-only. It is the selected
 * client's State (set on the client's Details tab) at that state's rate from
 * the Management page, so there is nothing to choose here. A resolved value is
 * shown as a chip with the rate as its strongest element; a state with no rate
 * or a client with no state is called out in warning colour.
 */
const StrategyTaxStateSetting = () => {
  const { settingsInfo } = useReportSettings();
  const clientId = settingsInfo.client_id ?? null;
  const { clientState, stateTaxRate, isLoading } = useStateTaxRate(clientId);

  const renderValue = () => {
    if (!clientId || isLoading) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="strategy-state-tax"
        >
          {clientId ? "Loading…" : "Select a client to see its state tax."}
        </Typography>
      );
    }
    if (!clientState) {
      return (
        <Typography
          variant="body2"
          color="warning.dark"
          data-testid="strategy-state-tax"
        >
          No state set — set it on the client's Details tab.
        </Typography>
      );
    }
    const stateLabel = getStateLabel(clientState);
    if (stateTaxRate == null) {
      return (
        <Box
          data-testid="strategy-state-tax"
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          <Chip label={stateLabel} variant="outlined" />
          <Typography variant="body2" color="warning.dark">
            {" — "}no tax rate set on the Management page
          </Typography>
        </Box>
      );
    }
    return (
      <Box data-testid="strategy-state-tax">
        <Chip
          color="primary"
          sx={{
            height: "auto",
            maxWidth: "100%",
            py: 0.5,
            px: 0.5,
            "& .MuiChip-label": { whiteSpace: "normal" },
          }}
          label={
            <>
              <Box
                component="span"
                sx={{ fontWeight: 500, fontSize: "0.875rem" }}
              >
                {stateLabel}
              </Box>
              <Box component="span" sx={{ opacity: 0.7 }}>
                {" — "}
              </Box>
              <Box
                component="span"
                sx={{ fontWeight: 700, fontSize: "1.125rem" }}
              >
                {`${stateTaxRate}%`}
              </Box>
            </>
          }
        />
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        State tax for strategy report
      </Typography>
      {renderValue()}
    </Box>
  );
};

export default StrategyTaxStateSetting;
